import { getFeatureFlags } from '../models/features/features';
import MojoApiFactory from '../config/api-factory';
import { programSelectors } from '../models/program';
import { useFeatureFlagsQuery } from './queries/use-feature-flags-query';
import { usePatronSelector } from './use-patron-selector';

/**
 * FE-38038: true once a flag read through `useFeatureFlag` can be trusted, so a caller can
 * hold a request back until a flag's value is actually known.
 *
 * `getFeatureFlag` returns `undefined` both when a flag is off and when the flags have not
 * arrived, because the reducer starts at `features: {}`. A query that only puts the flag in
 * its queryKey therefore fires once with the flag's fallback behaviour and again once it
 * resolves, and whichever response lands last wins in Redux.
 *
 * Waiting on the react-query `isFetched` alone is NOT enough, and that mistake is the reason
 * this reads Redux: patron-data dispatches `setFeatureFlags` from a `useEffect`, so it lands
 * a render AFTER the query reports fetched. A caller gated only on the query would still fire
 * while `useFeatureFlag` returned `undefined`.
 *
 * Fails open, deliberately, so a caller can be delayed but never blocked:
 *  - the flags request errored — nothing will ever populate Redux;
 *  - it succeeded but carried no flags — the dispatch cannot change `features`, so waiting
 *    for a key that will never appear would strand the caller;
 *  - there is no program — there is nothing to fetch.
 *
 * NOTE on the program id: the Redux `program` reducer starts at `null` and is populated by a
 * dispatch, so the selector returns `undefined` for the first renders of EVERY page load.
 * Treating that as "no program" made this report settled immediately and let a caller's mount
 * fetch fire before any flag was known — observed on FLY as a single `/feed` request with no
 * `mention_format`, which `useContentFeed` then never refetched because its effect also
 * requires `!loaded`. `MojoApiFactory.programId` is assigned once from `bootstrapData` in
 * src/index.tsx before React renders, so it tells "not loaded yet" apart from "genuinely no
 * program" — the latter being the only case that should fail open here. It also shares
 * patron-data's cache entry rather than adding a request: that query is keyed
 * ['featureFlags', program?.id || ''], which is the same id once Redux catches up.
 *
 * OUTAGE NOTE — why this hook must NEVER trigger a fetch. patron.jsx gates the ENTIRE routed
 * subtree on PatronData's `isFetching`, which ORs in `featureFlagsQuery.isFetching`, and the
 * QueryClient sets no `staleTime` (so default 0 = always stale). This hook is mounted by the
 * feed hooks, i.e. from INSIDE that gated subtree. An enabled observer therefore refetched on
 * every mount, which flipped `isFetching` true, which unmounted the subtree, which unmounted
 * this hook — and on the next render it mounted and refetched again. That loop blanked the
 * whole feed on FLY (spinner forever, `/features` called thousands of times, channel-detail
 * requests cancelled with net::ERR_ABORTED). Hence `enabled: false` below, plus a `staleTime`
 * on the query itself as defence in depth.
 */
export function useFeatureFlagsSettled(): boolean {
  const reduxProgramId = usePatronSelector(programSelectors.getProgramId);
  const programId = reduxProgramId || MojoApiFactory.programId;
  const flagsInStore = usePatronSelector(
    (state) => Object.keys(getFeatureFlags(state) ?? {}).length > 0
  );
  // `enabled: false` is LOAD-BEARING, not an optimization — see the OUTAGE note above.
  // This observer only ever READS the entry patron-data owns and fetches.
  const { isFetched, isError, data } = useFeatureFlagsQuery(
    programId ? String(programId) : '',
    { enabled: false }
  );

  if (!programId) return true;
  if (flagsInStore) return true;
  if (isError) return true;
  // NOTE: do NOT add a `!isFetching && !isFetched` fail-open here. It looks like the
  // "nothing will ever fetch" case but it is also the HYDRATED case: patron wraps the app in
  // PersistQueryClientProvider, so this query is restored from localStorage, and with the
  // staleTime above patron-data then has nothing to refetch — leaving isFetching false and
  // isFetched false while the data is already in hand. Failing open there fired the mount
  // fetch before patron-data's dispatch effect had populated Redux and brought the bare
  // `/feed` request straight back (observed on FLY on bundle index-BGOctMMP.js).
  // Blocking here is safe: patron-data enables its query off the same bootstrap program, so
  // if we have a programId it either fetched or hydrated, and its dispatch lands the flags in
  // Redux on the very next commit (child effects run before parent effects, so we are asked
  // one render early and re-evaluated as soon as the dispatch re-renders us).
  // Settled with nothing to dispatch: no key will ever appear in the store.
  return isFetched && Object.keys(data ?? {}).length === 0;
}

export default useFeatureFlagsSettled;
